import React, { useState } from "react"
import { Box } from "@mui/material"

import { dsColorNew } from "@palette"


// The logos live in the CDN folder `/logos`, named as the backend names a site's
// (`get_site_icon_dict`: `rentalcars.com` → `rentalcars.ico`). Providers have no
// logo of their own, but most exist there under their plain name (`click rent` →
// `clickrent.ico`, the provider's own site).
export const brandLogoKey = (name) => String(name || '')
	.split('.')[0]
	.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
	.toLowerCase()
	.replace(/[^a-z0-9_-]/g, '')

// Logos that are not in the CDN: once a logo has failed twice it is drawn as its initial for the session
const missing = new Set()

const initialOf = (name) => {
	const first = String(name || '').trim().split(/[\s._-]+/)[0] || ''
	return (first.length <= 2 ? first : first[0] || '?').toUpperCase()
}

/** A site's or a provider's logo at 16px; a grey circle with its initial when there is none. */
export default function AppBrandMark({ name, icon, size = 16, sx = {} }) {
	const file = icon || (brandLogoKey(name) ? `${brandLogoKey(name)}.ico` : '')
	// Per file, not per mount: tables reuse a cell for another provider when they sort or page, and a
	// logo that failed for the previous one must not hide this one's (Curro, 2026-10-19). One retry
	// before giving up, so a hiccup of the CDN does not cost the logo for the whole session.
	const [attempt, setAttempt] = useState({ file, n: 0 })
	const tries = attempt.file === file ? attempt.n : 0
	const failed = !file || missing.has(file) || tries > 1

	if (failed) return <Box component="span" aria-hidden sx={{
		width: size, height: size, borderRadius: '50%', flexShrink: 0,
		display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
		fontSize: size * 0.56, fontWeight: 700, lineHeight: 1, letterSpacing: '-0.2px',
		backgroundColor: dsColorNew.grayBorder, color: dsColorNew.textDark, ...sx,
	}}>
		{initialOf(name)}
	</Box>

	return <Box
		component="img"
		key={`${file}-${tries}`}
		src={tries ? `/logos/${file}?retry=1` : `/logos/${file}`}
		alt=""
		loading="lazy"
		onError={() => {
			if (tries >= 1) missing.add(file)
			setAttempt({ file, n: tries + 1 })
		}}
		sx={{ width: size, height: size, borderRadius: '3px', objectFit: 'contain', flexShrink: 0, ...sx }}
	/>
}

/** A provider as its logo and name, the way `AppRetailerBlock` shows a site. */
export function AppProviderBlock({ provider, sx = {} }) {
	if (!provider) return null
	return <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minWidth: 0, verticalAlign: 'middle', ...sx }}>
		<AppBrandMark name={provider}/>
		<Box component="span" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{provider}</Box>
	</Box>
}

/** A list of providers as small chips with their logo (instead of names joined with commas). */
export function AppProviderChips({ providers = [], sx = {} }) {
	return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ...sx }}>
		{providers.filter(Boolean).map((p, i) => <Box key={`${p}-${i}`} component="span" sx={{
			display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 24, pl: 0.75, pr: 1, borderRadius: '6px',
			border: `1px solid ${dsColorNew.grayBorder}`, backgroundColor: dsColorNew.white, fontSize: 12, color: dsColorNew.textTitle,
			whiteSpace: 'nowrap',
		}}>
			<AppBrandMark name={p}/>{p}
		</Box>)}
	</Box>
}
